import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, X, PlusCircle, Loader2, Building2, Check, ExternalLink, ShieldCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';

const ClinicSetup = () => {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = `${window.location.protocol}//${window.location.hostname}/api/clinics`;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchClinics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL, { headers });
      setClinics(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch clinics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClinics();
  }, []);

  const handleUpdate = async (id, updatedData) => {
    const toastId = toast.loading('Updating clinic...');
    try {
      await axios.put(`${API_URL}/${id}`, updatedData, { headers });
      toast.success('Clinic updated successfully!', { id: toastId });
      fetchClinics();
    } catch (err) {
      toast.error('Failed to update clinic', { id: toastId });
    }
  };

  const deleteClinic = async (id) => {
    if (!window.confirm('Are you sure you want to delete this clinic?')) return;
    const toastId = toast.loading('Deleting clinic...');
    try {
      await axios.delete(`${API_URL}/${id}`, { headers });
      toast.success('Clinic deleted!', { id: toastId });
      fetchClinics();
    } catch (err) {
      toast.error('Failed to delete clinic', { id: toastId });
    }
  };

  const addClinic = async () => {
    const toastId = toast.loading('Adding new clinic...');
    try {
      const newClinic = {
        code: `CL-${clinics.length + 101}`,
        name: 'New Clinical Facility',
        active: true,
        allBranches: true,
        branches: []
      };
      await axios.post(API_URL, newClinic, { headers });
      toast.success('New clinic added!', { id: toastId });
      fetchClinics();
    } catch (err) {
      toast.error('Failed to add clinic', { id: toastId });
    }
  };

  if (loading && clinics.length === 0) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
    </div>
  );

  return (
    <div className="p-6 bg-[#F8FAFC] min-h-screen text-slate-700 font-sans">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Compact Glassmorphism Header */}
        <div className="bg-white/80 backdrop-blur-md px-8 py-6 rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/40 flex justify-between items-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-indigo-500 via-sky-500 to-indigo-500" />
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200">
                    <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                    <h1 className="text-2xl font-black text-slate-800 tracking-tight">Clinic Setup</h1>
                    <p className="text-[11px] text-slate-400 font-black uppercase tracking-widest mt-1">Manage global hospital facilities</p>
                </div>
            </div>
            <button 
                onClick={addClinic}
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-black text-white rounded-2xl text-[12px] font-black uppercase tracking-widest shadow-xl shadow-indigo-100 transition-all active:scale-95 group"
            >
                <PlusCircle className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                Add New Clinic
            </button>
        </div>

        {/* Dense Professional Table */}
        <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden text-[12px]">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50/50">
              <tr>
                <th className="px-6 py-4 text-left font-black text-slate-400 uppercase tracking-widest">Code</th>
                <th className="px-6 py-4 text-left font-black text-slate-400 uppercase tracking-widest">Facility Name</th>
                <th className="px-6 py-4 text-center font-black text-slate-400 uppercase tracking-widest">Visibility</th>
                <th className="px-6 py-4 text-center font-black text-slate-400 uppercase tracking-widest">Active Status</th>
                <th className="px-6 py-4 text-right font-black text-slate-400 uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {clinics.map((item) => (
                <tr key={item.id} className="hover:bg-indigo-50/10 transition-all group">
                  <td className="px-6 py-3">
                    <input 
                      type="text" 
                      value={item.code} 
                      className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[11px] font-black text-indigo-600 uppercase tracking-wider focus:outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 focus:bg-white transition-all text-center"
                      onBlur={(e) => handleUpdate(item.id, { code: e.target.value })}
                      onChange={(e) => {
                         const val = e.target.value;
                         setClinics(clinics.map(t => t.id === item.id ? { ...t, code: val } : t));
                      }}
                    />
                  </td>
                  <td className="px-6 py-3 min-w-[300px]">
                    <div className="relative">
                        <input 
                            type="text" 
                            value={item.name} 
                            placeholder="Clinic Name..."
                            className="w-full px-4 py-2.5 bg-white border border-transparent rounded-xl text-[13px] font-bold text-slate-700 focus:outline-none focus:border-indigo-100 focus:bg-slate-50/50 transition-all group-hover:border-slate-100"
                            onBlur={(e) => handleUpdate(item.id, { name: e.target.value })}
                            onChange={(e) => {
                                const val = e.target.value;
                                setClinics(clinics.map(t => t.id === item.id ? { ...t, name: val } : t));
                            }}
                        />
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex justify-center">
                        <button 
                            onClick={() => handleUpdate(item.id, { allBranches: !item.allBranches })}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                                item.allBranches 
                                ? 'bg-sky-50 text-sky-600 border-sky-100' 
                                : 'bg-slate-50 text-slate-400 border-slate-100'
                            }`}
                        >
                            <ExternalLink className="w-3 h-3" />
                            {item.allBranches ? 'All Branches' : 'Limited'}
                        </button>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex justify-center">
                        <button 
                            onClick={() => handleUpdate(item.id, { active: !item.active })}
                            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-all duration-300 ${
                                item.active ? 'bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.3)]' : 'bg-slate-200'
                            }`}
                        >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition duration-300 ${
                                item.active ? 'translate-x-5.5' : 'translate-x-1'
                            }`} />
                        </button>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => deleteClinic(item.id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {clinics.length === 0 && (
            <div className="py-20 text-center space-y-3">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto text-slate-200">
                    <Building2 className="w-8 h-8" />
                </div>
                <div className="text-slate-300 font-black uppercase tracking-widest text-[10px]">No facilities configured yet</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClinicSetup;
